import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Palette, ShoppingBag, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import DesignPreview from "@/components/DesignPreview";
import { CUSTOM_TEE_PRICE } from "@/lib/constants";

export default function SharedDesign() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [design, setDesign] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/designs/share/${shareId}`).then(({ data }) => setDesign(data)).catch(() => setNotFound(true));
  }, [shareId]);

  if (notFound) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="shared-not-found">
        <h1 className="font-display font-bold text-4xl">Design not found</h1>
        <p className="font-semibold text-ink/70 mt-2">This share link may have expired.</p>
        <Link to="/designer" className="mt-6 inline-flex bg-ink text-paper font-bold px-6 py-3.5 rounded-xl neo-press border-2 border-ink">Open Design Studio</Link>
      </div>
    );
  }
  if (!design) return <div className="min-h-[60vh] grid place-items-center font-display font-bold text-xl animate-pulse">Loading…</div>;

  const addToCart = () => {
    addItem({
      key: `custom-${Date.now()}`, product_id: "custom", name: "Custom Designed Tee",
      image: null, price: CUSTOM_TEE_PRICE, size: design.size || "M",
      color: design.custom?.shirtColorName || "Custom", quantity: 1, custom: design.custom,
    });
    toast.success("Added to cart!");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="shared-design-page">
      <Link to="/shop" className="inline-flex items-center gap-2 font-bold mb-8 hover:text-brand-pink"><ArrowLeft className="w-4 h-4" /> Back to shop</Link>
      <div className="grid gap-8 lg:grid-cols-2 items-center">
        <div className="bg-brand-sky/40 neo rounded-3xl p-8 grid place-items-center">
          <DesignPreview custom={design.custom} className="w-full max-w-sm" />
        </div>
        <div>
          <span className="inline-block bg-brand-pink neo-sm rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider mb-3">Shared design</span>
          <h1 className="font-display font-bold text-4xl sm:text-5xl leading-tight">{design.name}</h1>
          <p className="mt-3 font-semibold text-ink/70">Someone fabricated this tee. Order it as-is or make it your own.</p>
          <p className="mt-4 font-display font-bold text-3xl bg-brand-yellow neo-sm rounded-lg inline-block px-3 py-1">${CUSTOM_TEE_PRICE}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <button onClick={addToCart} className="inline-flex items-center gap-2 bg-ink text-paper font-bold px-6 py-3.5 rounded-xl neo-press border-2 border-ink" data-testid="shared-add-to-cart">
              <ShoppingBag className="w-5 h-5" /> Add to Cart
            </button>
            <button onClick={() => navigate(`/designer?share=${shareId}`)} className="inline-flex items-center gap-2 bg-brand-mint font-bold px-6 py-3.5 rounded-xl neo-press border-2 border-ink" data-testid="shared-customize">
              <Palette className="w-5 h-5" /> Customize this
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
